import { axiosPrivate } from "../api/axiosCore";

// Every call goes to the CRM backend; the browser never talks to Live Chat directly.
const BASE = "api/livechat/conversations";
// the backend's strict JSON parser rejects a literal null body (400)
const EMPTY_BODY = {};

const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const call = async (request) => {
  try {
    return await request();
  } catch (error) {
    return error.response ?? { status: "No Server Response" };
  }
};

const withoutEmpty = (params) =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""));

export const serviceGetLivechatConversations = (token, filters = {}) =>
  call(() => axiosPrivate.get(BASE, { ...auth(token), params: withoutEmpty(filters) }));

export const serviceGetLivechatConversation = (token, conversationId) =>
  call(() => axiosPrivate.get(`${BASE}/${conversationId}`, auth(token)));

export const serviceGetLivechatMessages = (token, conversationId, before) =>
  call(() =>
    axiosPrivate.get(`${BASE}/${conversationId}/messages`, { ...auth(token), params: withoutEmpty({ before, limit: 50 }) }),
  );

// ponytail: 20 pages x 50 = the newest 1,000 messages; page in the UI if a ticket runs longer
const TRANSCRIPT_MAX_PAGES = 20;

// One ticket's transcript, oldest first. CRM-03 pages a conversation's history
// newest first across all its tickets: page back until past this ticket.
// A non-200 (403 outside your scope, 404, 429) is returned as is.
export const serviceGetLivechatTicketTranscript = async (token, conversationId, livechatTicketId) => {
  const transcript = [];
  let before;

  for (let page = 0; page < TRANSCRIPT_MAX_PAGES; page++) {
    const res = await serviceGetLivechatMessages(token, conversationId, before);
    if (res.status !== 200) return res;

    const { messages, next_before: nextBefore } = res.data;
    transcript.push(...messages.filter((message) => message.ticket_id === livechatTicketId));

    const isPastTicket = transcript.length > 0 && messages.at(-1)?.ticket_id !== livechatTicketId;
    if (!nextBefore || isPastTicket) break;
    before = nextBefore;
  }

  return { status: 200, data: transcript.reverse() };
};

export const serviceGetLivechatTickets = (token, conversationId) =>
  call(() => axiosPrivate.get(`${BASE}/${conversationId}/tickets`, auth(token)));

export const serviceGetLivechatNotes = (token, conversationId, ticketId, cursor) =>
  call(() =>
    axiosPrivate.get(`${BASE}/${conversationId}/notes`, { ...auth(token), params: withoutEmpty({ ticketId, cursor }) }),
  );

export const servicePostLivechatMessage = (token, conversationId, { clientId, text, replyToId, attachmentId, type }) =>
  call(() =>
    axiosPrivate.post(
      `${BASE}/${conversationId}/messages`,
      withoutEmpty({ clientId, text, replyToId, attachmentId, type }),
      auth(token),
    ),
  );

// Live Chat's admin upload settings for the conversation's site
export const serviceGetLivechatUploadLimits = (token, conversationId) =>
  call(() => axiosPrivate.get(`${BASE}/${conversationId}/upload-limits`, auth(token)));

// presigned urls for the browser to PUT the compressed bytes to
export const servicePostLivechatUpload = (token, conversationId, declaration) =>
  call(() => axiosPrivate.post(`${BASE}/${conversationId}/uploads`, withoutEmpty(declaration), auth(token)));

export const servicePostLivechatUploadComplete = (token, conversationId, uploadId) =>
  call(() => axiosPrivate.post(`${BASE}/${conversationId}/uploads/${uploadId}/complete`, EMPTY_BODY, auth(token)));

// validating -> ready | rejected (rejectReason)
export const serviceGetLivechatAttachmentStatus = (token, conversationId, attachmentId) =>
  call(() => axiosPrivate.get(`${BASE}/${conversationId}/attachments/${attachmentId}`, auth(token)));

// a fresh presigned link (1 hour) to one message's attachment
export const serviceGetLivechatAttachment = (token, conversationId, seq) =>
  call(() => axiosPrivate.get(`${BASE}/${conversationId}/messages/${seq}/attachment`, auth(token)));

// unsends one of the agent's own messages; answers the tombstone
export const serviceDeleteLivechatMessage = (token, conversationId, seq) =>
  call(() => axiosPrivate.delete(`${BASE}/${conversationId}/messages/${seq}`, auth(token)));

export const servicePostLivechatRead = (token, conversationId, throughSeq) =>
  call(() => axiosPrivate.post(`${BASE}/${conversationId}/read`, { throughSeq }, auth(token)));

export const servicePostLivechatTyping = (token, conversationId, typing) =>
  call(() => axiosPrivate.post(`${BASE}/${conversationId}/typing`, { typing }, auth(token)));

export const servicePutLivechatOpen = (token, conversationId) =>
  call(() => axiosPrivate.put(`${BASE}/${conversationId}/open`, EMPTY_BODY, auth(token)));

export const servicePutLivechatAssign = (token, conversationId, userId) =>
  call(() => axiosPrivate.put(`${BASE}/${conversationId}/assign/${userId}`, EMPTY_BODY, auth(token)));

// PENDING keeps the Live Chat ticket open, SOLVED closes it, OPENED reopens a closed one
export const servicePutLivechatStatus = (token, conversationId, status) =>
  call(() => axiosPrivate.put(`${BASE}/${conversationId}/status`, { status }, auth(token)));

export const servicePutLivechatResolve = (token, conversationId, sale) =>
  call(() => axiosPrivate.put(`${BASE}/${conversationId}/resolve`, sale, auth(token)));

export const servicePutLivechatTags = (token, conversationId, tagIds) =>
  call(() => axiosPrivate.put(`${BASE}/${conversationId}/tags`, { tagIds }, auth(token)));

// WhatsApp -> Live Chat: a single-use link plus the message to send it with
export const servicePostLivechatHandoff = (token, chatId, note) =>
  call(() => axiosPrivate.post("api/livechat/handoffs", withoutEmpty({ chatId, note }), auth(token)));
